import { makeAutoObservable } from 'mobx';
import { inject, injectable } from 'inversify';
import { IOsiSystemReportsWidgetViewModel } from '@shared/types/mobx/widgets/OsiSystemReports';
import logger from 'js-logger';
import { notifications } from '@shared/utils/notifications';
import { getDebtsByOsiId } from '@shared/api/osiSystemReports/report/debts/get';
import { getCollectionByOsiId } from '@shared/api/osiSystemReports/report/collection/get';
// OSI-798: клиент нового отчёта «по должникам на конец прошлого месяца»
import { getDebtsPrevMonthByOsiId } from '@shared/api/osiSystemReports/report/debtsPrevMonth/get';
import moment from 'moment/moment';
import { type OsiService, OsiServiceIdentifier } from '@shared/types/mobx/services/osi';
import { MIME, downloadBase64 } from '@shared/utils/download';

const tMainPrefix = 'systemReports';

// Идентификаторы отчётов (дискриминатор для ветвления при скачивании и per-report состояния)
const REPORT_ID_DEBTS = 1;
const REPORT_ID_COLLECTION = 2;
// OSI-798: третий отчёт — «Отчёт по должникам на конец прошлого месяца»
const REPORT_ID_DEBTS_PREV_MONTH = 3;

// OSI-798: значение Osi.PrintMonth, соответствующее схеме расчёта «месяц в месяц».
// Словарь на бэке (OSI.Core, Osi.PrintMonth): 1 — «Будущий», 0 — «Текущий» (месяц в месяц, дефолт), -1 — «Предыдущий».
// Отчёт по должникам на конец прошлого месяца имеет смысл ТОЛЬКО для домов с расчётом «месяц в месяц».
const PRINT_MONTH_CURRENT = 0;

@injectable()
export class OsiSystemReportsWidgetViewModel implements IOsiSystemReportsWidgetViewModel {
  // OSI-798: полный (нефильтрованный) список отчётов вкладки.
  // Наружу отдаётся через computed-геттер `reports`, который скрывает отчёты,
  // не предназначенные текущему дому. Само содержимое элементов не изменялось.
  private allReports: any[] = [
    {
      id: REPORT_ID_DEBTS,
      name: `${tMainPrefix}:debts.name`,
      comment: `${tMainPrefix}:debts.comment`,
      // MIME-тип исправлен: 'application/xlsx' не существует, WebKit на нём ведёт себя непредсказуемо
      contentType: MIME.XLSX,
      ext: 'xlsx',
      // Признак того, что для этой строки доступна опция «Свернуть дочерние помещения»
      hasCollapseOption: true
    },
    {
      // Второй отчёт — «Отчёт для взыскания» (collection)
      id: REPORT_ID_COLLECTION,
      name: `${tMainPrefix}:collection.name`,
      comment: `${tMainPrefix}:collection.comment`,
      // MIME-тип исправлен: 'application/xlsx' не существует, WebKit на нём ведёт себя непредсказуемо
      contentType: MIME.XLSX,
      ext: 'xlsx',
      hasCollapseOption: true
    },
    {
      // OSI-798: третий отчёт — «Отчёт по должникам на конец прошлого месяца» (debtsPrevMonth).
      // Контракт и формат выгрузки идентичны отчёту по должникам, отличается только эндпоинт.
      id: REPORT_ID_DEBTS_PREV_MONTH,
      name: `${tMainPrefix}:debtsPrevMonth.name`,
      comment: `${tMainPrefix}:debtsPrevMonth.comment`,
      // MIME-тип исправлен: 'application/xlsx' не существует, WebKit на нём ведёт себя непредсказуемо
      contentType: MIME.XLSX,
      ext: 'xlsx',
      hasCollapseOption: true
    }
  ];

  // OSI-798: список отчётов, реально отображаемый в таблице.
  // «Отчёт по должникам на конец прошлого месяца» показываем ТОЛЬКО домам с расчётом
  // «месяц в месяц» (Osi.PrintMonth === 0). Проверка строгая (===), а не truthy/!=:
  // значения -1 и 1 — валидные схемы («Предыдущий»/«Будущий»), для них отчёт скрыт.
  // Пока osiInfo ещё не загружен (undefined) — отчёт НЕ показываем. Это осознанное решение:
  // лучше показать строку позже (после прихода osiInfo), чем на мгновение показать её дому,
  // которому отчёт не предназначен. Геттер реактивный (см. ниже), поэтому строка появится сама.
  // Отчёты debts и collection не фильтруются — они по-прежнему доступны всем домам всегда.
  // makeAutoObservable в конструкторе превращает этот геттер в computed, а this.osiModule.osiInfo —
  // observable-поле OsiServiceImpl, поэтому его асинхронная загрузка триггерит пересчёт и перерисовку.
  get reports(): any[] {
    const isMonthInMonth = this.osiModule?.osiInfo?.printMonth === PRINT_MONTH_CURRENT;

    return this.allReports.filter(
      (report) => report.id !== REPORT_ID_DEBTS_PREV_MONTH || isMonthInMonth
    );
  }

  isLoading = false;

  // Состояние чекбокса «Свернуть дочерние помещения» — теперь ПО ОТЧЁТУ (per-report), а не общий флаг.
  // Причина: на вкладке два отчёта со своими чекбоксами, они НЕ должны быть связаны между собой.
  // Храним обычный объект-словарь { [reportId]: boolean }; makeAutoObservable делает его deep-observable,
  // поэтому мутация по существующему ключу триггерит перерисовку. Дефолт true для обоих отчётов
  // (дочерние помещения свёрнуты при открытии вкладки — как было у должников).
  collapseByReport: Record<number, boolean> = {
    [REPORT_ID_DEBTS]: true,
    [REPORT_ID_COLLECTION]: true,
    // OSI-798: свой независимый чекбокс для отчёта на конец прошлого месяца, дефолт — как у остальных
    [REPORT_ID_DEBTS_PREV_MONTH]: true
  };

  // Чтение per-report состояния чекбокса. Если ключа нет — считаем true (дефолт-сворачивание).
  isCollapseFor = (reportId: number): boolean => this.collapseByReport[reportId] ?? true;

  // Сеттер per-report состояния чекбокса
  setCollapseFor = (reportId: number, value: boolean) => {
    this.collapseByReport[reportId] = value;
  };

  constructor(@inject(OsiServiceIdentifier) private osiModule: OsiService) {
    makeAutoObservable(this);
  }

  // OSI-806: признак наличия у ОСИ дочерних помещений (паркинги/кладовки).
  // Источник правды — общий MobX-сервис OsiService (osiInfo.hasChildAbonents), критерий считает бэк
  // (активный абонент с parentAbonentId != null и areaTypeCode ∈ {PARKING, STORAGE}).
  // Нужен UI: в домах, где сворачивать нечего, колонка 'collapse' с чекбоксами «Свернуть дочерние
  // помещения» (все три отчёта) вообще НЕ добавляется в массив columns грида — не пустая ячейка,
  // а отсутствующая колонка, см. OsiSystemReportsWidget.tsx.
  // ВАЖНО: значения collapseByReport при скрытых чекбоксах НАМЕРЕННО НЕ сбрасываем (остаются true) —
  // дочерних помещений нет, поэтому query-параметр collapseChildren в выгрузках отчётов
  // (debts / collection / debtsPrevMonth) ничего не меняет.
  get hasChildAbonents(): boolean {
    return this.osiModule.osiInfo?.hasChildAbonents ?? false;
  }

  async onDownloadReportClick(report: any): Promise<void> {
    try {
      if (!this.osiModule?.osiId || !this.osiModule?.osiInfo?.name) return;

      this.isLoading = true;
      const startDate = moment(new Date()).startOf('month');
      const endDate = moment(new Date()).endOf('month');
      const format = 'YYYY-MM-DD[T]HH:mm:ss[Z]';

      const period = {
        begin: startDate.local().format(format),
        end: endDate.local().format(format)
      };

      // Берём флаг «Свернуть дочерние помещения» именно для ЭТОГО отчёта (per-report)
      const collapseChildren = this.isCollapseFor(report.id);

      // Ветвление по типу отчёта (report.id): должники → /debts, отчёт для взыскания → /collection,
      // OSI-798: должники на конец прошлого месяца → /debts-prev-month.
      // Ветка debts остаётся веткой по умолчанию (else) — поведение существующих отчётов не меняется.
      let data: any;
      if (report.id === REPORT_ID_COLLECTION) {
        data = await getCollectionByOsiId(this.osiModule.osiId, period, collapseChildren);
      } else if (report.id === REPORT_ID_DEBTS_PREV_MONTH) {
        data = await getDebtsPrevMonthByOsiId(this.osiModule.osiId, period, collapseChildren);
      } else {
        data = await getDebtsByOsiId(this.osiModule.osiId, period, collapseChildren);
      }

      // iOS/WebKit: data:-URL не скачивается по атрибуту download — переведено на blob: через downloadBase64
      downloadBase64(
        data as unknown as string,
        report.contentType,
        `${report.name}_${this.osiModule.osiInfo.name}_${period.begin.substring(0, 10)}_${period.end.substring(
          0,
          10
        )}.${report.ext}`
      );
    } catch (e) {
      logger.error(e);
      notifications.error();
    } finally {
      this.isLoading = false;
    }
  }
}
